// Making a character, on a stage of its own (like FFXIV or WoW): no town behind you, just your character on a
// little ink pedestal, big enough to see every detail. Four steps: your people, face and hair, clothes,
// then a name (checked as you type) and where you'll live. Turn the character with the arrows, a drag, Q/E,
// or the triggers; the camera moves in close for face and hair.
//
// Keyboard and controller: up/down picks a row, left/right changes it, LB/RB (or [ ]) change step, A/Enter
// goes on, B/Escape goes back, Y rolls a surprise.
import ink from '../ink/ink.js';
import { People, drawPerson, personTop, DIR_TH } from '../render/inkchar.js';
import { paintLook } from '../render/inkchar.js';
import { KIT, KIT_DEFAULT, type KitLook } from '../../../shared/kit.js';
import { APPEARANCE, BUILDS, EXPRESSIONS, FRAMES, HEIGHT_MAX, HEIGHT_MIN, RACE_LIST, raceInfo, type RaceId } from '../../../shared/races.js';
import { jobDef, type JobId } from '../../../shared/jobs.js';
import { FACTIONS } from '../../../shared/factions.js';
import { NATIONS, nationForRace, nationOfFaction } from '../../../shared/nations.js';
import { checkName } from '../../../shared/names.js';
import { sanitizeLook, type Look } from '../../../shared/look.js';

const I = ink as any;
const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };
const pick = <T>(a: readonly T[]) => a[Math.floor(Math.random() * a.length)];

export interface Created { id: number; name: string; faction: number; look: Look; lastSeen: string | null }
export interface CreatorHost {
  api<T>(path: string, method?: string, body?: unknown): Promise<{ status: number; data: T }>;
  sound(name: 'click' | 'open' | 'close' | 'error' | 'level' | 'blip'): void;
  created(c: Created, job: JobId): void;
  back(): void;
}

// (no job step: everyone starts as an Adventurer, and picks a job by talking to its mentor in the town square)
const STEPS = ['Your people', 'Face & hair', 'Clothes', 'Homeland', 'Name'];
const CLOAKS: [string, string][] = [['', 'None'], ['#6a8f5a', 'Moss'], ['#8a4a5a', 'Plum'], ['#4c5a80', 'Midnight'], ['#c9703a', 'Rust'], ['#e9dcc0', 'Linen'], ['#3e2f28', 'Ink']];
/** A soft wash behind each people, so the stage changes mood as you browse. */
const RACE_TINT: Record<RaceId, [string, string]> = {
  human: ['#f6e7c8', '#e4c99a'], elf: ['#e6f0dc', '#b9d3a6'], dwarf: ['#f3dcc4', '#d6a982'], halfling: ['#f8edc8', '#e2cc8a'],
  foxkin: ['#f8e0cc', '#e3a57e'], catkin: ['#f2e2ee', '#d4b2cc'], hornkin: ['#ece2f4', '#bba3d6'], mossfolk: ['#e2eed2', '#a9c58c'],
};
const NAME_A = ['Pip', 'Juni', 'Bram', 'Wren', 'Clo', 'Rook', 'Map', 'Tan', 'Moss', 'Fen', 'Oli', 'Sor', 'Bri', 'Hazel', 'Lark', 'Nim', 'Tam', 'Ivy', 'Rue', 'Ash'];
const NAME_B = ['per', 'ble', 'ver', 'ley', 'wick', 'ford', 'kin', 'ra', 'lo', 'mira', 'bell', 'wyn', 'sy', 'dell', 'ett', 'o', 'ie', 'ry'];

interface Row { id: string; label: string; kind: 'cycle' | 'swatch' | 'cards' | 'slider' | 'custom';
  options?: () => [string, string, string?][]; get?: () => string; set?: (v: string) => void; build?: (host: HTMLElement) => void }

export class Creator {
  active = false;
  private look: KitLook = { ...KIT_DEFAULT, wear: [] };
  private job: JobId = 'adventurer';
  private faction = FACTIONS[0].id;
  /** You picked a homeland yourself (until then it follows your people: elves to Thornwood, foxkin to Moonmere...). */
  private homePicked = false;
  private step = 0; private row = 0;
  private rot = 0; private spin = 0; private drag: { x: number; rot: number } | null = null;
  private zoom = 1; private lift = 0; private flourish = false;
  private people = new People();
  private raf = 0; private last = 0;
  private bg: HTMLCanvasElement | null = null;
  private motes: { x: number; y: number; v: number; s: number; p: number }[] = [];
  private nameOk: { name: string; ok: boolean; msg: string } | null = null; private nameT = 0; private nameSeq = 0;
  private padWas: boolean[] = []; private padRepeat = 0;
  private busy = false;
  /** The name box: kept in the page between steps so it keeps what you typed. */
  private nameIn = document.getElementById('crName') as HTMLInputElement;

  constructor(private host: CreatorHost) {
    $('crBack').addEventListener('click', () => this.go(-1));
    $('crNext').addEventListener('click', () => this.go(1));
    $('crRandom').addEventListener('click', () => { this.surprise(); this.host.sound('blip'); });
    $('crTurnL').addEventListener('click', () => { this.spin -= Math.PI / 4; });
    $('crTurnR').addEventListener('click', () => { this.spin += Math.PI / 4; });
    const cv = $<HTMLCanvasElement>('crStage');
    cv.addEventListener('pointerdown', e => { this.drag = { x: e.clientX, rot: this.rot }; cv.setPointerCapture(e.pointerId); });
    cv.addEventListener('pointermove', e => { if (this.drag) { this.rot = this.drag.rot + (e.clientX - this.drag.x) * 0.012; this.spin = 0; } });
    const up = () => { this.drag = null; };
    cv.addEventListener('pointerup', up); cv.addEventListener('pointercancel', up);
    addEventListener('keydown', e => this.key(e));
    addEventListener('resize', () => { this.bg = null; });
  }

  // ---------- opening and closing ----------
  open(): void {
    this.active = true; this.step = 0; this.row = 0; this.rot = 0; this.spin = 0; this.busy = false;
    this.surprise(true);
    $('creator').hidden = false; document.body.classList.add('creating');
    this.render();
    this.last = performance.now();
    cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(t => this.frame(t));
    this.host.sound('open');
  }
  close(): void { this.active = false; cancelAnimationFrame(this.raf); $('creator').hidden = true; document.body.classList.remove('creating'); }

  // ---------- the look ----------
  private app() { return APPEARANCE[this.look.race ?? 'human']; }
  private skins(): [string, string, string][] {
    const r = raceInfo(this.look.race);
    return r.skins ? r.skins.map((h, i) => [h, `Tone ${i + 1}`, h]) : Object.entries(KIT.SKINS).map(([k, h], i) => [k, `Tone ${i + 1}`, h]);
  }
  /** Change people: their own hair, face and jewelry lists, and skin tones that suit them. */
  private setRace(id: RaceId, keep = true): void {
    if (!this.homePicked) this.faction = nationForRace(id).faction;   // a people's homeland, unless you've chosen
    const was = this.look.race ?? 'human', a = APPEARANCE[id], oldA = APPEARANCE[was];
    const idx = (table: Record<string, string>, v: string | undefined) => Math.max(0, Object.keys(table).indexOf(v ?? ''));
    const at = <T extends Record<string, string>>(table: T, i: number) => Object.keys(table)[Math.min(i, Object.keys(table).length - 1)];
    const L = this.look;
    L.ihair = at(a.hair, keep ? idx(oldA.hair, L.ihair) : 0);
    L.facial = keep && L.facial && L.facial in a.face ? L.facial : 'none';
    L.jewel = keep ? at(a.jewel, idx(oldA.jewel, L.jewel)) : 'none';
    L.mark = keep ? at(a.mark, idx(oldA.mark, L.mark)) : 'none';
    L.race = id;
    const skins = this.skins().map(s => s[0]);
    if (!skins.includes(L.skin)) L.skin = skins[Math.min(skins.length - 1, 1)];
  }
  /** A whole new someone (or, opening, a nice starting point). */
  private surprise(first = false): void {
    const race = first ? pick(['human', 'elf', 'foxkin', 'catkin', 'halfling'] as RaceId[]) : pick(RACE_LIST).id;
    const tops = Object.keys(KIT.TOPS), bottoms = Object.keys(KIT.BOTTOMS), hairs = Object.keys(KIT.HAIRS);
    this.look = { ...KIT_DEFAULT, wear: [], hair: pick(hairs), top: pick(tops), bottom: pick(bottoms), frame: pick(['m', 'f']), build: Math.random() < 0.8 ? 'adult' : 'stout',
      height: 1, mood: pick(['neutral', 'happy', 'determined', 'shy', 'wink']) };
    this.look.race = race; this.setRace(race, false);
    const a = this.app();
    this.look.ihair = pick(Object.keys(a.hair)); this.look.skin = pick(this.skins()).at(0)!;
    if (Math.random() < 0.5) this.look.facial = pick(Object.keys(a.face));
    if (Math.random() < 0.6) this.look.jewel = pick(Object.keys(a.jewel));
    if (Math.random() < 0.6) this.look.mark = pick(Object.keys(a.mark));
    if (Math.random() < 0.3) { const c = pick(CLOAKS.slice(1)); this.look.wear = ['cloak']; this.look.cloak = c[0]; }
    if (first) { this.job = 'adventurer'; this.homePicked = false; this.faction = nationForRace(this.look.race).faction; this.randomName(); }
    this.render();
  }
  private randomName(): void { const n = pick(NAME_A) + pick(NAME_B); this.nameIn.value = n[0].toUpperCase() + n.slice(1); this.checkName(); }

  // ---------- steps and rows ----------
  private rows(): Row[] {
    const L = this.look, a = this.app();
    const cycle = (id: string, label: string, opts: () => [string, string][], get: () => string, set: (v: string) => void): Row => ({ id, label, kind: 'cycle', options: opts, get, set });
    switch (this.step) {
      case 0: return [
        { id: 'race', label: 'People', kind: 'cards', options: () => RACE_LIST.map(r => [r.id, r.name]), get: () => L.race ?? 'human', set: v => this.setRace(v as RaceId) },
        cycle('frame', 'Frame', () => FRAMES, () => L.frame ?? 'm', v => { L.frame = v; }),
        cycle('build', 'Build', () => BUILDS, () => L.build ?? 'adult', v => { L.build = v; }),
        { id: 'height', label: 'Height', kind: 'slider', get: () => String(L.height ?? 1), set: v => { L.height = Math.round(Number(v) * 100) / 100; } },
      ];
      case 1: return [
        cycle('hair', 'Hair', () => Object.entries(a.hair), () => L.ihair ?? Object.keys(a.hair)[0], v => { L.ihair = v; }),
        { id: 'haircol', label: 'Hair colour', kind: 'swatch', options: () => Object.entries(KIT.HAIRS).map(([k, h]) => [k, k, h]), get: () => L.hair, set: v => { L.hair = v; } },
        { id: 'skin', label: 'Skin', kind: 'swatch', options: () => this.skins(), get: () => L.skin, set: v => { L.skin = v; } },
        cycle('facial', 'Face', () => Object.entries(a.face), () => L.facial ?? 'none', v => { L.facial = v; }),
        cycle('mark', 'Markings', () => Object.entries(a.mark), () => L.mark ?? 'none', v => { L.mark = v; }),
        cycle('jewel', 'Jewelry', () => Object.entries(a.jewel), () => L.jewel ?? 'none', v => { L.jewel = v; }),
        cycle('mood', 'Expression', () => EXPRESSIONS, () => L.mood ?? 'neutral', v => { L.mood = v; }),
      ];
      case 2: return [
        { id: 'top', label: 'Top', kind: 'swatch', options: () => Object.entries(KIT.TOPS).map(([k, h]) => [k, k, h]), get: () => L.top, set: v => { L.top = v; } },
        { id: 'bottom', label: 'Trousers', kind: 'swatch', options: () => Object.entries(KIT.BOTTOMS).map(([k, h]) => [k, k, h]), get: () => L.bottom, set: v => { L.bottom = v; } },
        { id: 'cloak', label: 'Cloak', kind: 'swatch', options: () => CLOAKS.map(([h, n]) => [h, n, h || 'none']), get: () => L.wear.includes('cloak') ? L.cloak : '',
          set: v => { L.wear = L.wear.filter(w => w !== 'cloak'); if (v) { L.wear.push('cloak'); L.cloak = v; } } },
        cycle('headgear', 'Headgear', () => [['on', 'Shown'], ['off', 'Hidden']], () => L.headgear === false ? 'off' : 'on', v => { if (v === 'off') L.headgear = false; else delete L.headgear; }),
      ];
      case 3: return [
        { id: 'nation', label: 'Where do you call home?', kind: 'cards', options: () => NATIONS.map(n => [String(n.faction), `${n.emblem} ${n.name}`, n.color]), get: () => String(this.faction), set: v => { this.faction = Number(v); this.homePicked = true; } },
      ];
      default: return [
        { id: 'name', label: 'Name', kind: 'custom', build: h => this.nameRow(h) },
        { id: 'world', label: 'World', kind: 'custom', build: h => this.worldRow(h) },
      ];
    }
  }

  private render(): void {
    if (!this.active) return;
    const steps = $('crSteps'); steps.replaceChildren();
    STEPS.forEach((s, i) => {
      const b = el('button', 'cr-step' + (i === this.step ? ' on' : i < this.step ? ' done' : ''), ''); b.type = 'button';
      b.append(el('i', '', String(i + 1)), el('span', '', s));
      b.addEventListener('click', () => { if (i <= this.step || this.canLeave()) { this.step = i; this.row = 0; this.render(); this.host.sound('click'); } });
      steps.append(b);
    });
    $('crTitle').textContent = STEPS[this.step];
    $('creator').append(this.nameIn);   // (out of the rows before they're rebuilt)
    const box = $('crRows'); box.replaceChildren();
    const rows = this.rows(); this.row = Math.min(this.row, rows.length - 1);
    rows.forEach((r, ri) => {
      const row = el('div', 'cr-row' + (ri === this.row ? ' focus' : '') + ' ' + r.kind); row.tabIndex = -1;
      row.addEventListener('pointerdown', () => { if (this.row !== ri) { this.row = ri; for (const [j, x] of [...box.children].entries()) x.classList.toggle('focus', j === ri); } });
      row.append(el('span', 'cr-lab', r.label));
      const ctl = el('div', 'cr-ctl'); row.append(ctl);
      if (r.kind === 'cycle') {
        const opts = r.options!(), cur = Math.max(0, opts.findIndex(o => o[0] === r.get!()));
        const l = el('button', 'cr-arrow', '‹'), v = el('span', 'cr-val', opts[cur]?.[1] ?? ''), rr = el('button', 'cr-arrow', '›'), n = el('small', 'cr-n', `${cur + 1}/${opts.length}`);
        l.type = rr.type = 'button'; l.setAttribute('aria-label', `Previous ${r.label}`); rr.setAttribute('aria-label', `Next ${r.label}`);
        l.addEventListener('click', () => this.cycle(r, -1)); rr.addEventListener('click', () => this.cycle(r, 1));
        ctl.append(l, v, rr, n);
      } else if (r.kind === 'swatch') {
        for (const [k, name, hex] of r.options!()) {
          const b = el('button', 'cr-sw' + (r.get!() === k ? ' on' : '') + (hex === 'none' ? ' none' : '')); b.type = 'button'; b.title = name; b.setAttribute('aria-label', `${r.label}: ${name}`);
          if (hex && hex !== 'none') b.style.background = hex;
          b.addEventListener('click', () => { r.set!(k); this.changed(); });
          ctl.append(b);
        }
      } else if (r.kind === 'cards') {
        ctl.classList.add(r.id === 'race' ? 'cr-races' : r.id === 'job' ? 'cr-jobs' : r.id === 'nation' ? 'cr-nations' : 'cr-banners');
        for (const [k, name, color] of r.options!()) {
          const b = el('button', 'cr-card' + (r.get!() === k ? ' on' : '')); b.type = 'button';
          if (r.id === 'race') { const cv = el('canvas'); cv.width = 30; cv.height = 34; paintLook(cv, sanitizeLook({ ...this.preview(k as RaceId), headgear: false }), { job: this.job, key: 'cr-' + k + JSON.stringify(this.look) }); b.append(cv); }
          if (r.id === 'job') { const j = jobDef(k as JobId); b.style.setProperty('--job', j.color); b.append(el('b', '', name), el('small', '', j.role)); }
          else if (color) { const pip = el('i', 'cr-pip'); pip.style.background = color; b.append(pip, el('span', '', name)); }
          else b.append(el('span', '', name));
          b.addEventListener('click', () => { r.set!(k); this.changed(); });
          ctl.append(b);
        }
      } else if (r.kind === 'slider') {
        const s = el('input', 'cr-slider'); s.type = 'range'; s.min = String(HEIGHT_MIN); s.max = String(HEIGHT_MAX); s.step = '0.01'; s.value = r.get!();
        s.setAttribute('aria-label', r.label);
        s.addEventListener('input', () => { r.set!(s.value); this.info(); });
        ctl.append(s);
      } else r.build!(ctl);
      box.append(row);
    });
    this.info();
    $('crBack').textContent = this.step === 0 ? '‹ Characters' : '‹ Back';
    const last = this.step === STEPS.length - 1, next = $<HTMLButtonElement>('crNext');
    next.textContent = last ? (this.busy ? 'Creating…' : 'Begin your story') : 'Next ›';
    next.disabled = last && (this.busy || !this.nameOk?.ok);
    $('crRandom').hidden = last || this.step === 3;
    $('creator').style.setProperty('--home', nationOfFaction(this.faction).color);
  }
  /** Someone of another people with your colours, for the cards. */
  private preview(id: RaceId): KitLook {
    const keep = this.look, copy: KitLook = { ...keep, wear: [...keep.wear] };
    this.look = copy; this.setRace(id); this.look = keep;
    return copy;
  }
  private cycle(r: Row, d: number): void {
    const opts = r.options!(), cur = Math.max(0, opts.findIndex(o => o[0] === r.get!()));
    r.set!(opts[(cur + d + opts.length) % opts.length][0]); this.changed();
  }
  private changed(): void { this.host.sound('click'); this.render(); }

  /** The card on the right: about your people, your job, or a summary before you begin. */
  private info(): void {
    const box = $('crInfo'); box.replaceChildren();
    const r = raceInfo(this.look.race), home = nationOfFaction(this.faction);
    if (this.step === 3) {   // the homeland: its lore, and who settled it
      box.append(el('h3', '', `${home.emblem} ${home.name}`), el('div', 'cr-role', `${home.continent} · home town ${home.townName}`), el('p', '', home.blurb));
      for (const line of home.lore) box.append(el('p', 'cr-lore', line));
      box.append(el('p', 'cr-note', `Settled first by ${home.peoples.map(p => raceInfo(p).name.toLowerCase() + (raceInfo(p).name.endsWith('kin') || raceInfo(p).name.endsWith('folk') ? '' : 's')).join(', ')}. Anyone can live anywhere. You start here, and ferries cross to the other homelands.`));
      return;
    }
    if (this.step === STEPS.length - 1) {
      const nm = this.nameIn.value.trim() || 'Your name';
      box.append(el('h3', '', nm), el('div', 'cr-role', `${r.name} · ${jobDef(this.job).name} · ${home.name}`),
        el('p', '', `You'll wake up in your own home in ${home.townName}, in plain clothes. Say hello to the neighbours, then find a mentor in town: they'll teach you a job and hand you your first weapon.`));
      return;
    }
    box.append(el('h3', '', r.name), el('p', '', r.blurb));
    if (this.step === 0) box.append(el('p', 'cr-note', `Height ${Math.round((this.look.height ?? 1) * 100)}%`));
  }

  // ---------- name and world ----------
  private nameRow(h: HTMLElement): void {
    const wrap = el('div', 'cr-name'), input = this.nameIn, dice = el('button', 'cr-dice', '🎲'); dice.type = 'button'; dice.title = 'Another name';
    dice.addEventListener('click', () => { this.randomName(); this.host.sound('blip'); });
    wrap.append(input, dice);
    const st = el('p', 'cr-name-st'); st.id = 'crNameSt'; st.setAttribute('role', 'status');
    h.append(wrap, st); queueMicrotask(() => this.showName());   // (once the row is on the page)
    setTimeout(() => { if (this.active && this.step === STEPS.length - 1) input.focus(); }, 30);
  }
  private worldRow(h: HTMLElement): void {
    const card = el('div', 'cr-world on'), dot = el('i', 'dot');
    card.append(dot, el('b', '', 'Tiny Town'), el('small', '', 'checking…'));
    h.append(card);
    void this.host.api<{ players: number; capacity: number; queue: number }>('/api/status').then(r => {
      const d = r.data; if (r.status !== 200 || !d) { card.querySelector('small')!.textContent = 'Open'; return; }
      const fill = d.players / Math.max(1, d.capacity);
      dot.className = 'dot' + (d.queue || fill >= 1 ? ' full' : fill > 0.8 ? ' busy' : '');
      card.querySelector('small')!.textContent = d.queue ? `Busy · ${d.queue} waiting` : fill > 0.8 ? `Busy · ${d.players} playing` : `Open · ${d.players} playing · recommended`;
    }).catch(() => {});
    h.append(el('p', 'cr-note', 'More worlds will show up here as they open.'));
  }
  private checkName(): void {
    const v = this.nameIn.value, c = checkName(v), seq = ++this.nameSeq;
    clearTimeout(this.nameT);
    if (!c.ok) { this.nameOk = { name: v, ok: false, msg: v ? c.error : 'Pick a name.' }; this.showName(); return; }
    this.nameOk = { name: c.name, ok: false, msg: 'Checking…' }; this.showName();
    this.nameT = window.setTimeout(async () => {
      const r = await this.host.api<{ ok: boolean; error?: string }>(`/api/characters/name?n=${encodeURIComponent(c.name)}`).catch(() => null);
      if (seq !== this.nameSeq) return;   // they kept typing
      this.nameOk = !r || r.status !== 200 ? { name: c.name, ok: true, msg: `We'll check ${c.name} when you begin.` }
        : r.data.ok ? { name: c.name, ok: true, msg: `${c.name} is free.` } : { name: c.name, ok: false, msg: r.data.error ?? 'That name is taken.' };
      this.showName();
    }, 320);
  }
  private showName(): void {
    const st = document.getElementById('crNameSt'); if (st && this.nameOk) { st.textContent = (this.nameOk.ok ? '✓ ' : this.nameOk.msg === 'Checking…' ? '' : '✗ ') + this.nameOk.msg; st.className = 'cr-name-st ' + (this.nameOk.ok ? 'ok' : this.nameOk.msg === 'Checking…' ? '' : 'bad'); }
    if (this.step === STEPS.length - 1) { const next = $<HTMLButtonElement>('crNext'); next.disabled = this.busy || !this.nameOk?.ok; this.info(); }
  }
  /** Wire the name box once (it lives in the page so it keeps what you typed between steps). */
  wire(): void {
    const input = this.nameIn;
    input.addEventListener('input', () => this.checkName());
    input.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); this.go(1); } });
  }

  // ---------- moving through ----------
  private canLeave(): boolean { return true; }
  private go(d: number): void {
    if (this.busy) return;
    if (d < 0 && this.step === 0) { this.host.sound('close'); this.close(); this.host.back(); return; }
    if (d > 0 && this.step === STEPS.length - 1) { void this.create(); return; }
    this.step = Math.max(0, Math.min(STEPS.length - 1, this.step + d)); this.row = 0;
    this.host.sound(d > 0 ? 'open' : 'click'); this.render();
  }
  private async create(): Promise<void> {
    const look = sanitizeLook(this.look), name = this.nameIn.value.trim();
    if (!look) { this.nameOk = { name, ok: false, msg: 'Something about that look didn\'t work. Try Surprise me.' }; this.showName(); return; }
    if (!this.nameOk?.ok) { this.host.sound('error'); return; }
    this.busy = true; this.render();
    const r = await this.host.api<Created & { error?: string }>('/api/characters', 'POST', { name, faction: this.faction, look, job: this.job }).catch(() => ({ status: 0, data: { error: 'Could not reach the server.' } as Created & { error?: string } }));
    this.busy = false;
    if (r.status !== 201) { this.nameOk = { name, ok: false, msg: r.data.error || 'Could not create that character.' }; this.host.sound('error'); this.render(); return; }
    this.host.sound('level');
    this.close();
    this.host.created(r.data, this.job);
  }

  private key(e: KeyboardEvent): void {
    if (!this.active) return;
    const typing = (e.target as HTMLElement)?.tagName === 'INPUT' && (e.target as HTMLInputElement).type === 'text';
    if (typing && !['Escape', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    const k = e.key;
    if (k === 'ArrowUp' || k === 'ArrowDown') { e.preventDefault(); this.moveRow(k === 'ArrowUp' ? -1 : 1); }
    else if (k === 'ArrowLeft' || k === 'ArrowRight') { e.preventDefault(); this.nudge(k === 'ArrowLeft' ? -1 : 1); }
    else if (k === 'q' || k === 'Q') this.spin -= Math.PI / 4;
    else if (k === 'e' || k === 'E') this.spin += Math.PI / 4;
    else if (k === '[' || k === ']') this.go(k === '[' ? -1 : 1);
    else if (k === 'Enter' && !(e.target as HTMLElement)?.closest?.('button')) { e.preventDefault(); this.go(1); }
    else if (k === 'Escape') { e.preventDefault(); if (typing) (e.target as HTMLElement).blur(); else this.go(-1); }
    else if ((k === 'r' || k === 'R') && this.step < STEPS.length - 1) this.surprise();
  }
  private moveRow(d: number): void {
    const rows = this.rows(); this.row = (this.row + d + rows.length) % rows.length;
    const box = $('crRows'); [...box.children].forEach((x, j) => x.classList.toggle('focus', j === this.row));
    (box.children[this.row] as HTMLElement)?.scrollIntoView({ block: 'nearest' });
    if (rows[this.row].id === 'name') this.nameIn.focus(); else (document.activeElement as HTMLElement)?.blur?.();
    this.host.sound('blip');
  }
  /** Left/right on the focused row. */
  private nudge(d: number): void {
    const r = this.rows()[this.row]; if (!r) return;
    if (r.kind === 'slider') { r.set!(String(Math.min(HEIGHT_MAX, Math.max(HEIGHT_MIN, Number(r.get!()) + d * 0.02)))); this.changed(); return; }
    if (r.kind === 'custom') return;
    this.cycle(r, d);
  }

  // ---------- the controller ----------
  private pollPad(dt: number): void {
    const p = [...(navigator.getGamepads?.() ?? [])].find(g => g && g.connected && g.mapping === 'standard'); if (!p) return;
    const down = (i: number) => !!p.buttons[i]?.pressed, edge = (i: number) => { const n = down(i), e = n && !this.padWas[i]; this.padWas[i] = n; return e; };
    if (edge(0)) this.go(1); if (edge(1)) this.go(-1); if (edge(3) && this.step < STEPS.length - 1) this.surprise();
    if (edge(4)) this.go(-1); if (edge(5)) this.go(1); if (edge(9)) this.go(1);
    const ay = p.axes[1] ?? 0, ax = p.axes[0] ?? 0, dir = down(12) || ay < -0.6 ? 'u' : down(13) || ay > 0.6 ? 'd' : down(14) || ax < -0.6 ? 'l' : down(15) || ax > 0.6 ? 'r' : '';
    if (dir) { this.padRepeat -= dt; if (this.padRepeat <= 0) { this.padRepeat = this.padRepeat < -1 ? 0.12 : 0.32; if (dir === 'u' || dir === 'd') this.moveRow(dir === 'u' ? -1 : 1); else this.nudge(dir === 'l' ? -1 : 1); } }
    else this.padRepeat = 0;
    const turn = (p.buttons[7]?.value ?? 0) - (p.buttons[6]?.value ?? 0) + (Math.abs(p.axes[2] ?? 0) > 0.2 ? p.axes[2] : 0);
    if (Math.abs(turn) > 0.1) { this.rot += turn * dt * 2.4; this.spin = 0; }
  }

  // ---------- the stage ----------
  private frame(now: number): void {
    if (!this.active) return;
    const dt = Math.min(0.05, (now - this.last) / 1000); this.last = now;
    this.pollPad(dt);
    if (this.spin) { const s = Math.sign(this.spin) * Math.min(Math.abs(this.spin), dt * 6); this.rot += s; this.spin -= s; }
    const cv = $<HTMLCanvasElement>('crStage'), dpr = Math.min(2, devicePixelRatio || 1), W = Math.round(cv.clientWidth * dpr), H = Math.round(cv.clientHeight * dpr);
    if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; this.bg = null; }
    const g = cv.getContext('2d')!;
    I.setTime(now / 1000); I.stepFx(dt);
    g.setTransform(1, 0, 0, 1, 0, 0);
    const race = (this.look.race ?? 'human') as RaceId;
    if (!this.bg || this.bg.dataset.race !== race) this.bg = this.paintBg(W, H, race);
    g.drawImage(this.bg, 0, 0);
    // the camera: close on the face for face and hair, otherwise the whole of them
    const face = this.step === 1, narrow = cv.clientWidth < 860, zoomT = face ? (narrow ? 1.35 : 1.65) : this.step === STEPS.length - 1 ? 0.92 : 1;
    this.zoom += (zoomT - this.zoom) * Math.min(1, dt * 5);
    // (no hat or helmet while you pick hair and face: the job's gear goes on at the job step)
    const shown = this.step < 2 ? { ...this.look, headgear: false } : this.look;
    const c = this.people.step('cr', shown, { job: this.job }, 0, 0, 0, this.flourish, dt, now, null);
    this.flourish = false;
    c.th = c.thT = DIR_TH[0] + 0.38 + this.rot;
    const top = personTop(c), mobile = narrow, base = (mobile ? Math.min(H * 0.3, W * 0.62) : H * 0.6) / 26;
    const k = base * this.zoom, cx = W * 0.5, feet = mobile ? H * 0.5 : H * 0.8;
    const liftT = face ? H * (mobile ? 0.3 : 0.5) + (top - 10) * k - feet : 0; this.lift += (liftT - this.lift) * Math.min(1, dt * 5);
    const fy = feet + this.lift;
    // the pedestal: a soft pool of light, an ink ring and a few flowers round the edge
    g.save(); g.translate(cx, fy);
    const pr = 18 * k / Math.max(1, this.zoom ** 0.6);
    const glow = g.createRadialGradient(0, 0, 0, 0, 0, pr * 1.8); glow.addColorStop(0, 'rgba(255,250,236,0.95)'); glow.addColorStop(1, 'rgba(255,250,236,0)');
    g.fillStyle = glow; g.beginPath(); g.ellipse(0, 0, pr * 1.8, pr * 0.7, 0, 0, Math.PI * 2); g.fill();
    g.scale(k / 4, k / 4);
    I.oval(g, 0, 6, 74, 22, '#efe2c6', 41, 2.4, 22); I.oval(g, 0, 2, 70, 19, '#f8eedb', 42, 2.4, 22);
    g.strokeStyle = 'rgba(62,47,40,0.25)'; g.lineWidth = 1.5; g.beginPath(); g.ellipse(0, 2, 52, 13, 0, 0, Math.PI * 2); g.stroke();
    for (let i = 0; i < 7; i++) { const a = i / 7 * Math.PI * 2 + 0.4; if (Math.sin(a) < -0.3) continue; I.flowerBed(g, Math.cos(a) * 70, 8 + Math.sin(a) * 19, 11, I.mulberry(900 + i), 900 + i); }   // (none behind them)
    g.restore();
    drawPerson(g, c, cx, fy, 0, k, now, false);
    // drifting motes in the light
    if (this.motes.length < 26) this.motes.push({ x: Math.random() * W, y: H * (0.3 + Math.random() * 0.7), v: 8 + Math.random() * 14, s: 1 + Math.random() * 2.2, p: Math.random() * 6 });
    for (const m of this.motes) {
      m.y -= m.v * dt * dpr; m.x += Math.sin(now / 1300 + m.p) * 6 * dt * dpr;
      if (m.y < H * 0.05) { m.y = H * (0.6 + Math.random() * 0.4); m.x = Math.random() * W; }
      g.fillStyle = `rgba(255,248,226,${0.35 + 0.35 * Math.sin(now / 700 + m.p)})`; g.beginPath(); g.arc(m.x, m.y, m.s * dpr, 0, Math.PI * 2); g.fill();
    }
    this.raf = requestAnimationFrame(t => this.frame(t));
  }
  /** The backdrop, painted once: a warm wash in the people's colours, soft hills, a vignette, paper grain. */
  private paintBg(W: number, H: number, race: RaceId): HTMLCanvasElement {
    const c = document.createElement('canvas'); c.width = W; c.height = H; c.dataset.race = race;
    const g = c.getContext('2d')!, [a, b] = RACE_TINT[race];
    const sky = g.createLinearGradient(0, 0, 0, H); sky.addColorStop(0, a); sky.addColorStop(1, b); g.fillStyle = sky; g.fillRect(0, 0, W, H);
    // hills, far to near
    const hill = (y: number, amp: number, col: string, seed: number) => {
      g.fillStyle = col; g.beginPath(); g.moveTo(0, H);
      for (let x = 0; x <= W + 20; x += 20) g.lineTo(x, y + Math.sin(x / W * 5 + seed) * amp + Math.sin(x / W * 13 + seed * 2) * amp * 0.3);
      g.lineTo(W, H); g.closePath(); g.fill();
      g.strokeStyle = 'rgba(62,47,40,0.18)'; g.lineWidth = Math.max(1.5, W / 700); g.stroke();
    };
    hill(H * 0.62, H * 0.04, I.mixc(b, '#ffffff', 0.35), 1.3);
    hill(H * 0.72, H * 0.035, I.mixc(b, '#ffffff', 0.15), 4.1);
    hill(H * 0.84, H * 0.02, I.mixc(b, '#3e2f28', 0.08), 2.2);
    const sun = g.createRadialGradient(W * 0.5, H * 0.3, 0, W * 0.5, H * 0.3, H * 0.6); sun.addColorStop(0, 'rgba(255,252,240,0.75)'); sun.addColorStop(1, 'rgba(255,252,240,0)');
    g.fillStyle = sun; g.fillRect(0, 0, W, H);
    const vig = g.createRadialGradient(W / 2, H * 0.55, Math.min(W, H) * 0.35, W / 2, H * 0.55, Math.max(W, H) * 0.8); vig.addColorStop(0, 'rgba(62,47,40,0)'); vig.addColorStop(1, 'rgba(62,47,40,0.35)');
    g.fillStyle = vig; g.fillRect(0, 0, W, H);
    g.fillStyle = 'rgba(62,47,40,0.035)';
    for (let i = 0; i < W * H / 900; i++) g.fillRect(Math.random() * W, Math.random() * H, 1.2, 1.2);
    return c;
  }
}
